import { useNavigate } from "react-router-dom";
import { Globe, Smartphone, ShieldCheck, Server, Trash2, CheckSquare, Square, Lock } from "lucide-react";
import ScoreGauge, { scoreColor } from "@/components/ScoreGauge";

const TechBadge = ({ children }) => (
  <span className="bg-white/5 border border-white/10 text-zinc-300 font-mono text-[11px] px-2 py-0.5 rounded">
    {children}
  </span>
);

export default function DomainCard({ item, onDelete, selected, onToggleSelect, locked, onUnlock }) {
  const navigate = useNavigate();
  const r = item.last_result || item.result || item;
  const scores = r?.scores || {};
  const fe = r?.frontend || {};
  const sec = r?.security || {};
  const domain = item.domain || r?.domain;
  const headersVerified = Boolean(
    sec.headersRead !== false && sec.hsts && sec.xFrameOptions &&
    sec.xContentTypeOptions && sec.referrerPolicy && sec.permissionsPolicy
  );

  return (
    <div
      data-testid={`domain-card-${domain}`}
      className="group bg-[#121212] border border-white/10 rounded-xl p-5 hover:border-white/25 hover:-translate-y-[2px] transition-all duration-200 cursor-pointer relative"
      onClick={() => navigate(`/domain/${encodeURIComponent(domain)}`)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <button
              data-testid={`select-domain-${domain}`}
              onClick={(e) => { e.stopPropagation(); onToggleSelect?.(domain); }}
              className="text-zinc-500 hover:text-[#007AFF] transition-colors"
            >
              {selected ? <CheckSquare size={16} className="text-[#007AFF]" /> : <Square size={16} />}
            </button>
            <h3 className="font-heading font-semibold text-white text-base truncate">{domain}</h3>
          </div>
          <p className="font-mono text-xs text-zinc-500 mt-1 truncate">
            {r?.ip || "unresolved"} · {r?.cdn && r.cdn !== "None detected" ? r.cdn : "no CDN"}
          </p>
        </div>
        <ScoreGauge value={scores.integration} size={64} stroke={6} />
      </div>

      <div className="flex flex-wrap gap-1.5 mt-4">
        <TechBadge>{fe.framework || "unknown"}</TechBadge>
        {fe.reactVersion && <TechBadge>React {fe.reactVersion}</TechBadge>}
        {fe.usesjQuery && <TechBadge>jQuery</TechBadge>}
        {r?.ssl?.valid ? <TechBadge>SSL ✓</TechBadge> : <TechBadge>No SSL</TechBadge>}
        {headersVerified && (
          <span
            data-testid={`headers-verified-${domain}`}
            title="All 5 security headers present: HSTS, X-Frame-Options, X-Content-Type-Options, Referrer-Policy, Permissions-Policy"
            className="bg-[#32D74B]/10 border border-[#32D74B]/30 text-[#32D74B] font-mono text-[11px] px-2 py-0.5 rounded inline-flex items-center gap-1"
          >
            <ShieldCheck size={11} /> Headers 5/5
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/5">
        {[
          { icon: Globe, label: "FE", val: scores.frontend },
          { icon: Smartphone, label: "Mobile", val: scores.mobile },
          { icon: ShieldCheck, label: "Sec", val: scores.security },
        ].map((m) => (
          <div key={m.label} className="flex items-center gap-1.5">
            <m.icon size={14} style={{ color: scoreColor(m.val) }} />
            <span className="font-mono text-xs" style={{ color: scoreColor(m.val) }}>{m.val ?? "--"}</span>
          </div>
        ))}
      </div>

      {locked && (
        <button
          data-testid={`unlock-domain-${domain}`}
          onClick={(e) => { e.stopPropagation(); onUnlock?.(domain); }}
          className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold btn-brand text-white transition-colors"
          title="Unlock the full report + AI build brief for this website"
        >
          <Lock size={13} /> Unlock report + brief — $29
        </button>
      )}

      <button
        data-testid={`delete-domain-${domain}`}
        onClick={(e) => { e.stopPropagation(); onDelete?.(domain); }}
        className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-[#FF3B30] transition-all"
        title="Remove"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}
